import AbstractSpatialFilterControl from "./AbstractSpatialFilterControl";

import type LayerViewModel from "@esWebAPI/es/map/LayerViewModel";
import type { Criteria } from "@esWebAPI/es/model/Criteria";
import type { Attribute } from "@esWebAPI/es/model/Feature";

const PREFIX = "ES_WITHIN_DIST_";
const MAX_KMS = 1600;
const MAX_FEET_US = 5000000;
const MAX_FEET = 5000000;
const MAX_METERS = 1600000;
const MAX_MILES = 1000;
const IS_VALID_NUMBER_REGEXP = new RegExp("^\\+?(([0-9]+\\.?[0-9]*)|(\\.[0-9]+))$");

export default class WithinDistanceFilterControl extends AbstractSpatialFilterControl {
    private name: string;
    private geometry?: any[];
    private geoShapeKey?: string;
    private unit: string;
    private distance: number;

    constructor(layerViewModel: LayerViewModel, geometry?: any[], geoShapeKey?: string) {
        super(layerViewModel, "WithinDistance", $i18n('Search_Within_Distance'));

        this.name = this.generateUniqName(PREFIX);
        this.geometry = geometry;
        this.geoShapeKey = geoShapeKey;
        this.unit = es.util.getDefaultDistanceUnit();
        this.distance = 0;
    };

    toCriteria(): Criteria | null {
        if (!this.distance) {
            return null;
        }

        const criteria: Criteria = {
            $type: this.getType(),
            name: this.name,
            distance: this.distance,
            unit: this.unit
        };
        this.setGeometry(criteria, this.geoShapeKey, this.geometry);

        return criteria;
    }

    getDynamicAttributes(): JQueryDeferred<Attribute[]> {
        const attr: Attribute = {
            $type: "CalculateDistanceAttribute",
            name: this.name,
            criteriaName: this.name,
            title: "Distance (" + $i18n("_Distance_Unit_" + this.unit) + ")",
            type: "DECIMAL",
            unit: this.unit
        };
        this.setGeometry(attr, this.geoShapeKey, this.geometry);

        return es.util.deferred.resolved([attr]);
    }

    fromCriteria(criteria: Criteria) {
        if (criteria.$type !== this.getType()) {
            es.log.error("Invalid criteria given");
            return;
        }

        this.distance = criteria.distance;
        this.unit = criteria.unit;
        this.name = criteria.name;
        if (criteria.geoShapeKey) {
            this.geoShapeKey = criteria.geoShapeKey;
        } else {
            this.geometry = criteria.geometry;
        }
    }

    checkValues(distance: number, unit: string): string | null {
        if (!IS_VALID_NUMBER_REGEXP.test(distance) || isNaN(distance) || distance <= 0) { // last 2 conditions should never happen, regexp should fail... keeping it for now...
            return $i18n('_Filter_Error_Msg_Not_Supported');
        }

        if (unit === "SURVEY_FOOT" && distance > MAX_FEET_US) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_FEET_US + " " + $i18n('_Feet_US'));
        }

        if (unit === "FOOT" && distance > MAX_FEET) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_FEET + " " + $i18n('Feet'));
        }

        if (unit === "METER" && distance > MAX_METERS) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_METERS + " " + $i18n('Meters'));
        }

        if (unit === "KILOMETER" && distance > MAX_KMS) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_KMS + " " + $i18n('Kms'));
        }

        if (unit === "MILE" && distance > MAX_MILES) {
            return ($i18n('_Limits_Buffers_WW_Error_Msg_Above_Maximum') + MAX_MILES + " " + $i18n('Miles'));
        }

        return null;
    };

    isValid(): boolean {
        return this.checkValues(this.distance, this.unit) === null;
    };

    update(distance: number | string, unit: string) {
        if (unit != this.unit) {
            es.util.setDefaultDistanceUnit(unit);
        }
        
        distance = parseFloat(distance);
        
        var err = this.checkValues(distance, unit);
        if (err) {
            return err;
        }
        
        this.distance = distance;
        this.unit = unit;

        this.eventChannel.fireEvent("onUpdate");
    };

    static supportCriteria(criteria: Criteria): boolean {
        return criteria.$type === "WithinDistance";
    }

    static supportAttribute(): boolean {
        return false;
    }

    static fromCriteria(layerViewModel: LayerViewModel, criteria: Criteria): JQueryDeferred<WithinDistanceFilterControl> {
        return new WithinDistanceFilterControl(layerViewModel).init().done((filter: WithinDistanceFilterControl) => {
            filter.fromCriteria(criteria);
        });
    }

    // es.control.WithinDistanceFilter = WithinDistanceFilter;
}
